/**
 * Auth store for AI-Manual.
 *
 * Trimmed from Poranos: dropped legacy per-brand user_type variants,
 * dropped can_access_* permission fields, dropped landingPath / homePath
 * brand-routing, dropped Poranos Desktop iframe modal state. AI-Manual has
 * a single user_type='user' and a single home page (/manual).
 */
import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import * as Sentry from "@sentry/vue";

import { fetchWithAuth } from "@/api/fetchApi";
import { postAuthWithToken } from "@/api/authApi";
import router from "@/router";

// ポップアップ方式サインインの上限待ち時間。IdP の COOP で「ユーザーが閉じた」を
// 検知できないことがあるため、UI を必ず解放するための保険（§_signInWithOAuthPopup）。
// MFA やパスワード入力に時間がかかる人を切らない程度には長く取る。
const POPUP_SIGN_IN_TIMEOUT_MS = 3 * 60 * 1000;

export const useAuthStore = defineStore("auth", () => {
  const user = ref(null);
  watch(user, (newUser) => {
    if (newUser?.uid) {
      Sentry.setUser({ id: newUser.uid });
    } else {
      Sentry.setUser(null);
    }
  });
  const userEmail = computed(() => user.value?.email);
  const isAuthenticated = ref(false);
  const isGuest = computed(
    () => user.value?.email?.startsWith("guest@") ?? false,
  );
  const initialized = ref(false);

  // Loaded from /api/auth/account-detail/. Populated lazily after auth.
  const userProfile = ref({
    id: null,
    uid: "",
    email: "",
    display_name: "",
    user_type: "user",
    is_guest: false,
    is_staff: false,
    organization: null, // { slug, brand_name, logo_url, primary_color, ... }
    role: "viewer",
    status: "active",
    dept_tags: [],
    _loaded: false,
  });

  /**
   * backend が認証を拒否した（招待されていない / 無効化済み）場合の後始末。
   * Firebase からサインアウトしないと、SPA だけログイン状態のまま全 API が
   * 401 になり続ける。
   * @returns {boolean} 拒否エラーとして処理したら true
   */
  async function handleAuthRejection(error) {
    if (error?.authCode !== "not_invited" && error?.authCode !== "account_disabled") {
      return false;
    }
    try {
      await signOut(getAuth());
    } catch (signOutError) {
      console.error("[authStore] signOut after rejection failed:", signOutError);
    }
    user.value = null;
    isAuthenticated.value = false;
    alert(error.message);
    return true;
  }

  async function loadUserSettings() {
    try {
      const data = await fetchWithAuth("/auth/account-detail/");
      if (data && data.user) {
        userProfile.value = { ...data.user, _loaded: true };
      }
    } catch (err) {
      console.error("[authStore] account-detail failed:", err);
    }
  }

  async function signup(email, password, displayName = null) {
    try {
      const auth = getAuth();
      const credential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      user.value = await postAuthWithToken(
        credential.user,
        "/register/",
        displayName,
      );
      isAuthenticated.value = true;
      await loadUserSettings();
    } catch (error) {
      if (!(await handleAuthRejection(error))) {
        alert(_friendlyAuthMessage(error, "サインアップに失敗しました"));
      }
      throw error;
    }
  }

  async function login(email, password) {
    try {
      const auth = getAuth();
      const credential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      user.value = await postAuthWithToken(credential.user);
      isAuthenticated.value = true;
      await loadUserSettings();
    } catch (error) {
      if (!(await handleAuthRejection(error))) {
        alert(_friendlyAuthMessage(error, "ログインに失敗しました"));
      }
      throw error;
    }
  }

  /**
   * OAuth プロバイダ共通のサインイン。**popup を使う（redirect ではない）**。
   *
   * signInWithRedirect は認証状態を authDomain（*.firebaseapp.com）側の
   * ストレージに置いて戻ってくるため、**サードパーティ Cookie / ストレージを
   * 制限するブラウザでは戻った時点で状態が失われ、getRedirectResult() が
   * エラーではなく null を返す**（＝無言でログイン画面に戻る）。
   * dev の Chrome 150 で Google / Microsoft とも再現した。
   *
   * popup は window.opener 経由で直接やり取りするのでこの制限を受けない。
   * poranos は COOP(same-origin) のため popup を使えず redirect にしているが、
   * ai-manual に COOP/COEP は無いので popup を選べる。
   * → **COOP を導入する場合はここが壊れる**。その場合は authDomain を自ドメインに
   *   し、/__/auth/ を *.firebaseapp.com へ逆プロキシする必要がある
   *   （CloudFront のクエリ/Cookie 転送設定の確認が要る）。
   */
  async function _completeOAuthSignIn(result) {
    const firebaseUser = result.user;
    const displayName =
      firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "";
    user.value = await postAuthWithToken(firebaseUser, "/login/", displayName);
    isAuthenticated.value = true;
    await loadUserSettings();
  }

  async function _signInWithOAuthPopup(provider) {
    const auth = getAuth();
    const pending = signInWithPopup(auth, provider);
    let timedOut = false;
    try {
      // IdP 側（Microsoft 等）が COOP を返すとポップアップとの opener 関係が
      // 切れ、SDK の popup.closed ポーリングが働かない。その状態でユーザーが
      // 自分でウィンドウを閉じると signInWithPopup が永久に解決せず、呼び出し元の
      // busy が解除されずボタンが固まる。タイムアウトで必ず解放する。
      const result = await Promise.race([
        pending,
        new Promise((resolve) =>
          setTimeout(() => {
            timedOut = true;
            resolve(null);
          }, POPUP_SIGN_IN_TIMEOUT_MS),
        ),
      ]);

      if (timedOut) {
        console.warn("[authStore] popup sign-in timed out; releasing UI");
        // 諦めた後に遅れて解決した場合（ユーザーが時間をかけただけだった）は
        // 取りこぼさずログインを完了させる。
        pending
          .then(async (late) => {
            if (!late) return;
            await _completeOAuthSignIn(late);
            router.push("/manual");
          })
          .catch(() => {});
        return false;
      }

      await _completeOAuthSignIn(result);
      return true;
    } catch (error) {
      if (await handleAuthRejection(error)) return false;
      // ユーザーが自分で閉じた / 連打した場合は黙って戻す。
      if (
        error?.code === "auth/popup-closed-by-user" ||
        error?.code === "auth/cancelled-popup-request"
      ) {
        return false;
      }
      console.error("[authStore] popup sign-in failed:", error);
      alert(_friendlyAuthMessage(error, "ログインに失敗しました"));
      return false;
    }
  }

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return _signInWithOAuthPopup(provider);
  }

  async function loginWithMicrosoft() {
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({ prompt: "select_account" });
    return _signInWithOAuthPopup(provider);
  }

  /** Google / Microsoft のリダイレクト復帰を共通で処理する。 */
  async function handleRedirectResult() {
    const auth = getAuth();
    try {
      const result = await getRedirectResult(auth);
      if (!result) return false;

      const firebaseUser = result.user;
      const displayName =
        firebaseUser.displayName ||
        firebaseUser.email?.split("@")[0] ||
        "";
      user.value = await postAuthWithToken(
        firebaseUser,
        "/login/",
        displayName,
      );
      isAuthenticated.value = true;
      await loadUserSettings();
      return true;
    } catch (error) {
      if (await handleAuthRejection(error)) return false;
      console.error("[authStore] redirect result error:", error);
      // login() / signup() と違いここは握り潰していたため、リダイレクト系
      // （Google / Microsoft）の失敗がユーザーに何も表示されず、ログイン画面に
      // 戻るだけの行き止まりになっていた。プロバイダを env で有効化したが
      // Firebase コンソール側が未設定だと auth/operation-not-allowed で必ずここに来る。
      alert(_friendlyAuthMessage(error, "ログインに失敗しました"));
      return false;
    }
  }

  async function logout() {
    try {
      const auth = getAuth();
      await signOut(auth);
      user.value = null;
      userProfile.value = {
        id: null,
        uid: "",
        email: "",
        display_name: "",
        user_type: "user",
        is_guest: false,
        is_staff: false,
        organization: null,
        role: "viewer",
        status: "active",
        dept_tags: [],
        _loaded: false,
      };
      isAuthenticated.value = false;
      router.push("/login");
    } catch (error) {
      console.error("[authStore] logout failed:", error.message);
    }
  }

  async function sendPasswordReset(email) {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      alert(`${email} あてにパスワード再設定用のメールを送信しました`);
    } catch (error) {
      console.error("[authStore] password reset failed:", error);
      alert("パスワードリセットに失敗しました: " + error.message);
    }
  }

  async function deleteAccount() {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      alert("ログインユーザーが見つかりません");
      return;
    }
    try {
      await fetchWithAuth("/auth/delete-account/", { method: "DELETE" });
      try {
        await deleteUser(firebaseUser);
      } catch (firebaseError) {
        // Backend already deleted the Firebase user — ignore.
        console.log(
          "[authStore] Firebase user already deleted:",
          firebaseError.code,
        );
      }
      await logout();
    } catch (error) {
      console.error("[authStore] deleteAccount failed:", error);
      // 最後の admin ガード（400 non_field_errors）を「再ログインが必要」と
      // 表示していたため原因が伝わらなかった。UserAdmin.vue と同じく
      // backend のメッセージを優先して出す。
      alert(
        error?.non_field_errors?.[0] ||
          error?.detail ||
          "アカウント削除に失敗しました。再ログインが必要かもしれません。",
      );
    }
  }

  /**
   * Initialise the auth state on app boot. Resolves once
   * onAuthStateChanged fires once (cached creds or anonymous).
   */
  function initAuth() {
    return new Promise((resolve) => {
      const auth = getAuth();
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          user.value = firebaseUser;
          isAuthenticated.value = true;
          await loadUserSettings();
        } else {
          user.value = null;
          isAuthenticated.value = false;
        }
        initialized.value = true;
        resolve();
      });
    });
  }

  function isOwnedByCurrentUser(record) {
    return user.value?.email === record?.owner_email;
  }

  return {
    user,
    userEmail,
    isAuthenticated,
    isGuest,
    initialized,
    userProfile,
    signup,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    handleRedirectResult,
    logout,
    sendPasswordReset,
    deleteAccount,
    loadUserSettings,
    initAuth,
    isOwnedByCurrentUser,
    handleAuthRejection,
  };
});

function _friendlyAuthMessage(error, fallback) {
  switch (error?.code) {
    case "auth/email-already-in-use":
      return "このメールアドレスはすでに登録されています";
    case "auth/invalid-email":
      return "メールアドレスの形式が正しくありません";
    case "auth/weak-password":
      return "パスワードは6文字以上にしてください";
    case "auth/missing-password":
      return "パスワードを入力してください";
    case "auth/user-not-found":
    case "auth/invalid-credential":
      return "ログインできません。メールアドレスとパスワードをご確認ください";
    case "auth/wrong-password":
      return "パスワードが間違っています";
    case "auth/too-many-requests":
      return "ログイン試行が多すぎます。しばらくしてから再試行してください";
    case "auth/internal-error":
      return "内部エラーが発生しました。もう一度お試しください";
    case "auth/operation-not-allowed":
      return "この操作は許可されていません（管理者にお問い合わせください）";
    default:
      return `${fallback}: ${error?.message ?? error}`;
  }
}
